import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ExpandCollapse } from '@tds/core-expand-collapse';

import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import request from '../utils/request';
import BarChart from './barChart';
import Table from '../design/shared/table';
import { Box, Flex } from './css';
import { getFormattedDate } from '../utils/utils';
import TypeAhead from '../orderDetails/components/typeAhead';

const Analytics = ({ agents }) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    ORDERS: { orderId, lastCaseComment },
    general: { loading },
    srtDashboard: {
      status: {
        pendingValidation,
        waitingOnCredit,
        waitingOnSales,
        contractSent,
        withConcierge,
        contractExpired
      }
    },
    analytics: {
      analytics,
      selectTheView,
      total,
      oldestOrder,
      orderStatus,
      noOfDays,
      timestamp,
      totalCount
    }
  } = localizedStrings;
  const initOptions = [
    {
      label: '',
      id: ''
    },
    {
      label: total,
      id: total
    }
  ];
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(initOptions);
  const [statsBarChart, setStatsBarChart] = useState();
  const [statsTable, setStatsTable] = useState();
  const [selectedOption, setSelectedOption] = useState();

  const bars = [
    {
      dataKey: pendingValidation,
      color: '#0A84A5'
    },
    {
      dataKey: waitingOnCredit,
      color: '#F6C85F'
    },
    {
      dataKey: waitingOnSales,
      color: '#6F4D7C'
    },
    {
      dataKey: contractSent,
      color: '#9ED866'
    },
    {
      dataKey: withConcierge,
      color: '#CA472F'
    },
    {
      dataKey: contractExpired,
      color: '#6E838E'
    }
  ];

  const tableHeader = {
    oldestOrder,
    orderId,
    timestamp,
    noOfDays,
    lastCaseComment
  };

  const getStats = userId => {
    if (!userId) {
      setStatsBarChart(null);
      setStatsTable(null);
      setSelectedOption(null);
      return;
    }

    let url = '/orders/stats';
    if (userId && userId !== total) {
      url = url.concat('?assigned_user_id=', userId);
    }
    setIsLoading(true);
    request
      .get(url)
      .then(response => response.data)
      .then(data => {
        const {
          STATUS_COUNT,
          OLDEST_STATUS_ORDER: {
            PENDING_VALIDATION,
            WAITING_ON_CREDIT,
            WAITING_ON_SALES,
            CONTRACT_SENT,
            WITH_CONCIERGE,
            CONTRACT_EXPIRED
          }
        } = data;
        const newStatsBarChart = [
          {
            name: orderStatus,
            [pendingValidation]: STATUS_COUNT.PENDING_VALIDATION,
            [waitingOnCredit]: STATUS_COUNT.WAITING_ON_CREDIT,
            [waitingOnSales]: STATUS_COUNT.WAITING_ON_SALES,
            [contractSent]: STATUS_COUNT.CONTRACT_SENT,
            [withConcierge]: STATUS_COUNT.WITH_CONCIERGE,
            [contractExpired]: STATUS_COUNT.CONTRACT_EXPIRED
          }
        ];
        const newStatsTable = [
          {
            oldestOrder: pendingValidation,
            orderId: PENDING_VALIDATION.order_id,
            timestamp: getFormattedDate(PENDING_VALIDATION.status_changed_at),
            noOfDays: PENDING_VALIDATION.number_of_days,
            lastCaseComment: PENDING_VALIDATION.last_comment
          },
          {
            oldestOrder: waitingOnCredit,
            orderId: WAITING_ON_CREDIT.order_id,
            timestamp: getFormattedDate(WAITING_ON_CREDIT.status_changed_at),
            noOfDays: WAITING_ON_CREDIT.number_of_days,
            lastCaseComment: WAITING_ON_CREDIT.last_comment
          },
          {
            oldestOrder: waitingOnSales,
            orderId: WAITING_ON_SALES.order_id,
            timestamp: getFormattedDate(WAITING_ON_SALES.status_changed_at),
            noOfDays: WAITING_ON_SALES.number_of_days,
            lastCaseComment: WAITING_ON_SALES.last_comment
          },
          {
            oldestOrder: contractSent,
            orderId: CONTRACT_SENT.order_id,
            timestamp: getFormattedDate(CONTRACT_SENT.status_changed_at),
            noOfDays: CONTRACT_SENT.number_of_days,
            lastCaseComment: CONTRACT_SENT.last_comment
          },
          {
            oldestOrder: withConcierge,
            orderId: WITH_CONCIERGE.order_id,
            timestamp: getFormattedDate(WITH_CONCIERGE.status_changed_at),
            noOfDays: WITH_CONCIERGE.number_of_days,
            lastCaseComment: WITH_CONCIERGE.last_comment
          },
          {
            oldestOrder: contractExpired,
            orderId: CONTRACT_EXPIRED.order_id,
            timestamp: getFormattedDate(CONTRACT_EXPIRED.status_changed_at),
            noOfDays: CONTRACT_EXPIRED.number_of_days,
            lastCaseComment: CONTRACT_EXPIRED.last_comment
          }
        ];
        const agent = options.find(item => item.id === userId);
        const option = {
          label: agent.label,
          id: userId
        };

        setStatsBarChart(newStatsBarChart);
        setStatsTable(newStatsTable);
        setSelectedOption(option);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!agents) return;

    const newOptions = options.concat(
      agents.map(agent => {
        return {
          label: agent.label,
          id: agent.id
        };
      })
    );
    setOptions(newOptions);
  }, [agents]);

  const onChange = selected => {
    getStats(selected && selected.length && selected[0].id);
  };

  return (
    <ExpandCollapse topDivider={false}>
      <ExpandCollapse.Panel id="filters" header={analytics}>
        {!agents ? (
          <h2>{loading}</h2>
        ) : (
          <Flex>
            <Box width="55%">
              <TypeAhead
                label={selectTheView}
                placeholder=""
                options={options}
                selected={selectedOption ? [selectedOption] : []}
                onChange={onChange}
              />
              {statsTable && (
                <>
                  <hr />
                  <Table header={tableHeader} data={statsTable} />
                </>
              )}
            </Box>
            <Box width="35%">
              {isLoading ? (
                <h2>{loading}</h2>
              ) : (
                <>
                  {statsBarChart && (
                    <BarChart
                      stats={statsBarChart}
                      totalCount={totalCount}
                      bars={bars}
                    />
                  )}
                </>
              )}
            </Box>
          </Flex>
        )}
      </ExpandCollapse.Panel>
    </ExpandCollapse>
  );
};

Analytics.propTypes = {
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired
};

export default Analytics;
